// Angular
import { BehaviorSubject, takeUntil } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

// Libraries

import { TranslateService } from '@ngx-translate/core';

// Services
import { CoreService } from './core.service';

// Models
import { Job } from '../../modules/job/models';
import { DeviceDto } from '../../modules/job/models/device-dto.model';
import { AppCleanup } from '../models';
import { SyncResponseLv } from '../models';

// Enums
import { StatusType } from '../enums';

export interface JobSyncEvent {
	key: string;
	syncResponse: SyncResponseLv;
	showToaster: boolean;
	showJobSelector: boolean;
}

@Injectable({
	providedIn: 'root',
})
export class SyncService extends AppCleanup {
	isConnected = true;
	private syncUrl: string;
	protected currentJobSyncedVar: BehaviorSubject<JobSyncEvent> = new BehaviorSubject<JobSyncEvent>(null);
	currentJobSynced$ = this.currentJobSyncedVar.asObservable();

	protected latestSyncDateVar: BehaviorSubject<Date> = new BehaviorSubject<Date>(null);
	latestSyncDate$ = this.latestSyncDateVar.asObservable();

	protected latestSyncSpinnerVar: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
	latestSyncSpinner$ = this.latestSyncSpinnerVar.asObservable();

	protected latestSyncStatusVar: BehaviorSubject<SyncStatusType> = new BehaviorSubject<SyncStatusType>(null);
	latestSyncStatus$ = this.latestSyncStatusVar.asObservable();

	protected fullSyncDateVar: BehaviorSubject<Date> = new BehaviorSubject<Date>(null);
	fullSyncDate$ = this.fullSyncDateVar.asObservable();

	protected fullSyncSpinnerVar: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
	fullSyncSpinner$ = this.fullSyncSpinnerVar.asObservable();

	protected fullSyncStatusVar: BehaviorSubject<SyncStatusType> = new BehaviorSubject<SyncStatusType>(null);
	fullSyncStatus$ = this.fullSyncStatusVar.asObservable();

	showSyncSpinner = false;

	constructor(
		private http: HttpClient,
		private coreService: CoreService,
		private translateService: TranslateService
	) {
		super();

		this.syncUrl = `${this.syncApiUrl}api/sync`;

		this.isConnected = window.navigator.onLine;
	}

	setConnection(isConnected: boolean) {
		this.isConnected = isConnected;
	}

	private _syncJob(jobId: number, syncType: 'all' | 'latest') {
		return this.http.get<SyncResponseLv>(`${this.syncUrl}/${jobId}?syncType=${syncType}`);
	}

	_syncData(syncType: 'all' | 'latest') {
		return this.http.get<SyncResponseLv[]>(`${this.syncUrl}?syncType=${syncType}`);
	}

	syncJob(job: Job, syncType: 'all' | 'latest', showToaster: boolean, showJobSelector: boolean) {
		if (job) {
			this.syncJobWithKey(job.key, syncType, showToaster, showJobSelector);
		}
	}

	syncJobWithKey(key: string, syncType: 'all' | 'latest', showToaster: boolean, showJobSelector: boolean) {
		this.showSyncSpinner = true;
		this._syncJob(parseInt(key), syncType)
			.pipe(takeUntil(this.destroy))
			.subscribe((response) => {
				if (response) {
					this.triggerSyncMessage(key, response, showToaster, showJobSelector);
					this.showSyncSpinner = false;
				} else {
					const message = `${this.translateService.instant('GENERIC.SYNC_FAILED')}`;
					this.coreService.toast({ message: message, type: StatusType.DANGER });
					this.showSyncSpinner = false;
				}
			});
	}

	private _syncJobWithNewGuid(key: string, jobGuid: string) {
		return this.http.get<SyncResponseLv>(`${this.syncUrl}/new-guid?key=${key}&jobGuid=${jobGuid}`);
	}

	syncJobWithNewGuid(key: string, jobGuid: string) {
		this.showSyncSpinner = true;
		this._syncJobWithNewGuid(key, jobGuid)
			.pipe(takeUntil(this.destroy))
			.subscribe((response) => {
				if (response) {
					this.triggerSyncMessage(key, response, true, false);
					this.showSyncSpinner = false;
				} else {
					const message = `${this.translateService.instant('GENERIC.SYNC_FAILED')}`;
					this.coreService.toast({ message: message, type: StatusType.DANGER });
					this.showSyncSpinner = false;
				}
			});
	}

	triggerSyncMessage(key: string, syncResponse: SyncResponseLv, showToaster: boolean, showJobSelector: boolean) {
		this.currentJobSyncedVar.next({
			key: key,
			syncResponse: syncResponse,
			showToaster: showToaster,
			showJobSelector: showJobSelector,
		});
	}

	getMachineName() {
		return this.http.get<DeviceDto>(`${this.syncUrl}/machine-name`);
	}

	cleanJob(id: number) {
		return this.http.delete(`${this.syncUrl}/${id}`);
	}

	setLatestSyncDate(date: Date) {
		this.latestSyncDateVar.next(date);
	}

	getLatestSyncDate() {
		return this.latestSyncDateVar;
	}

	setLatestSyncSpinner(spinner) {
		this.latestSyncSpinnerVar.next(spinner);
	}

	setLatestSyncStatus(status) {
		this.latestSyncStatusVar.next(status);
	}

	setFullSyncDate(date: Date) {
		this.fullSyncDateVar.next(date);
	}

	setFullSyncSpinner(spinner: boolean) {
		this.fullSyncSpinnerVar.next(spinner);
	}

	setFullSyncStatus(status) {
		this.fullSyncStatusVar.next(status);
	}

	sync(syncType: 'all' | 'latest' = 'all') {
		if (syncType === 'all') {
			this.setFullSyncSpinner(true);
		} else {
			this.setLatestSyncSpinner(true);
		}

		this._syncData(syncType).subscribe({
			next: (response) => {
				if (response.length > 0) {
					if (syncType === 'all') {
						this.setFullSyncDate(response[0].syncDate);
						this.setFullSyncStatus(SyncStatusType.SUCCESS);
						this.setFullSyncSpinner(false);
					} else {
						this.setLatestSyncDate(response[0].syncDate);
						this.setLatestSyncStatus(SyncStatusType.SUCCESS);
						this.setLatestSyncSpinner(false);
					}

					let type: StatusType = StatusType.WARNING;
					let message: string = '';
					if (response[0].isFailedCommonSync) {
						message = `${this.translateService.instant('GENERIC.SYNC_SUCCESSFULL_COMMON_FAILED')}`;
					} else if (response[0].isFailedJobSync) {
						message = `${this.translateService.instant('GENERIC.SYNC_SUCCESSFULL_JOB_FAILED')}`;
					} else {
						message = `${this.translateService.instant('GENERIC.HAS_SYNCED')}`;
						type = StatusType.SUCCESS;
					}
					this.coreService.toast({ message: message, type: type });
				}
			},
			error: () => {
				if (syncType === 'all') {
					this.setFullSyncSpinner(false);
					this.setFullSyncStatus(SyncStatusType.FAILURE);
				} else {
					this.setLatestSyncSpinner(false);
					this.setLatestSyncStatus(SyncStatusType.FAILURE);
				}

				const message = `${this.translateService.instant('GENERIC.SYNC_FAILED')}`;
				this.coreService.toast({ message: message, type: StatusType.DANGER });
			},
		});
	}
}

export enum SyncStatusType {
	SUCCESS = 'green',
	FAILURE = 'red',
	WARNING = 'yellow',
}
